// Purchasing → Vendors (plan chunk C9, decision 184): who we buy from, how to reach them, their terms and the days they
// deliver; added, changed, put away and brought back by the owner and the office. Prices and papers stay where they are.
import type { Vendor, VendorCreate } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { Empty, ErrorBox, QueryView } from '../components/ui';
import { formatPhone, weekdayName } from '../format';
import { useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const DAYS = [1, 2, 3, 4, 5, 6, 7];
const KINDS = ['wholesaler', 'grower', 'retail', 'other'] as const;
type Form = { name: string; kind: Vendor['kind']; phone: string; email: string; termsDays: string; deliveryDays: number[]; notes: string };
const blank: Form = { name: '', kind: 'wholesaler', phone: '', email: '', termsDays: '', deliveryDays: [], notes: '' };
const fromVendor = (v: Vendor): Form => ({ name: v.name, kind: v.kind, phone: v.phone ?? '', email: v.email ?? '', termsDays: v.termsDays === null ? '' : String(v.termsDays), deliveryDays: v.deliveryDays, notes: v.notes ?? '' });
const toBody = (f: Form): VendorCreate => ({
  name: f.name.trim(),
  kind: f.kind,
  phone: f.phone.trim() || null,
  email: f.email.trim() || null,
  termsDays: f.termsDays.trim() === '' ? null : Math.max(0, Math.round(Number(f.termsDays))),
  deliveryDays: f.deliveryDays,
  notes: f.notes.trim() || null,
});

/** terms typed that are not a number of days (0 to 365): Save waits, nothing is cleared */
const termsBad = (f: Form) => f.termsDays.trim() !== '' && !(Number(f.termsDays) >= 0 && Number(f.termsDays) <= 365);

function VendorFields({ f, set, idPrefix }: { f: Form; set: (n: Partial<Form>) => void; idPrefix: string }) {
  const { t, intlLocale } = useLocale();
  const badTerms = termsBad(f);
  return (
    <div className="form-grid">
      <label className="field">
        <span>{t('name')}</span>
        <input type="text" value={f.name} required maxLength={120} autoComplete="off" onChange={(e) => set({ name: e.target.value })} />
      </label>
      <label className="field">
        <span>{t('vendorKind')}</span>
        <select value={f.kind} onChange={(e) => set({ kind: e.target.value as Form['kind'] })}>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`vendorKind_${k}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t('phone')}</span>
        <input type="tel" value={f.phone} maxLength={40} autoComplete="off" onChange={(e) => set({ phone: e.target.value })} />
      </label>
      <label className="field">
        <span>{t('email')}</span>
        <input type="email" value={f.email} maxLength={200} autoComplete="off" onChange={(e) => set({ email: e.target.value })} />
      </label>
      <label className="field">
        <span>{t('vendorTermsDays')}</span>
        <input type="text" inputMode="numeric" value={f.termsDays} aria-invalid={badTerms || undefined} onChange={(e) => set({ termsDays: e.target.value })} />
      </label>
      <fieldset className="field">
        <legend>{t('deliveryDays')}</legend>
        <div className="chips" role="group" aria-label={`${t('deliveryDays')} ${idPrefix}`}>
          {DAYS.map((d) => (
            <button key={d} type="button" className="chip" aria-pressed={f.deliveryDays.includes(d)} onClick={() => set({ deliveryDays: f.deliveryDays.includes(d) ? f.deliveryDays.filter((x) => x !== d) : [...f.deliveryDays, d].sort((a, b) => a - b) })}>
              {weekdayName(d, intlLocale)}
            </button>
          ))}
        </div>
      </fieldset>
      <label className="field" style={{ gridColumn: '1 / -1' }}>
        <span>{t('notes')}</span>
        <textarea rows={2} value={f.notes} maxLength={2000} onChange={(e) => set({ notes: e.target.value })} />
      </label>
    </div>
  );
}

function VendorRow({ v, archived }: { v: Vendor; archived: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState<Form>(() => fromVendor(v));
  const refresh = () => void qc.invalidateQueries({ queryKey: ['vendors'] });
  const save = useMutation({ mutationFn: () => api.patchVendor(v.id, toBody(f)), onSuccess: () => { setOpen(false); refresh(); } });
  const away = useMutation({ mutationFn: (on: boolean) => api.archiveVendor(v.id, on), onSuccess: refresh });
  return (
    <li className="count-row stack-sm" data-testid="vendor-row">
      <div className="row count-head">
        <span className="row-main">
          <span className="row-title">{v.name}</span>
          <span className="row-sub">
            {t(`vendorKind_${v.kind}`)}
            {v.phone ? ` · ${formatPhone(v.phone)}` : ''}
            {v.email ? ` · ${v.email}` : ''}
            {v.termsDays !== null ? ` · ${t('vendorTermsDays')} ${v.termsDays}` : ''}
            {v.deliveryDays.length ? ` · ${v.deliveryDays.map((d) => weekdayName(d, intlLocale)).join(', ')}` : ''}
          </span>
          {v.notes ? <span className="row-sub">{v.notes}</span> : null}
        </span>
      </div>
      {/* under the name, so a phone keeps the name whole */}
      <div className="contact-actions">
          {archived ? (
            <button type="button" className="btn btn-primary" disabled={away.isPending} onClick={() => away.mutate(false)}>
              {t('restoreBtn')}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => { setF(fromVendor(v)); setOpen((x) => !x); }}>
                {t('editDetails')}
              </button>
              <button type="button" className="btn btn-ghost" disabled={away.isPending} onClick={() => { if (window.confirm(t('archiveVendorConfirm').replace('{name}', v.name))) away.mutate(true); }}>
                {t('archive')}
              </button>
            </>
          )}
      </div>
      {away.error ? <ErrorBox error={away.error} /> : null}
      {open ? (
        <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); if (f.name.trim()) save.mutate(); }}>
          <VendorFields f={f} set={(n) => setF((c) => ({ ...c, ...n }))} idPrefix={v.name} />
          {save.error ? <ErrorBox error={save.error} /> : null}
          <div className="contact-actions">
            <button type="submit" className="btn btn-primary" disabled={save.isPending || !f.name.trim() || termsBad(f)}>
              {t('save')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>
              {t('cancel')}
            </button>
          </div>
        </form>
      ) : null}
    </li>
  );
}

export function VendorsPage() {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [archivedParam, setArchived] = useSearchParam('archived');
  const archived = archivedParam === '1';
  const [q, setQ] = useState('');
  const book = useQuery({ queryKey: qk.vendorBook(archived), queryFn: ({ signal }) => api.vendorBook(archived, signal) });
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState<Form>(blank);
  const add = useMutation({
    mutationFn: () => api.createVendor(toBody(f)),
    onSuccess: () => {
      setF(blank);
      setAdding(false);
      void qc.invalidateQueries({ queryKey: ['vendors'] });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (f.name.trim()) add.mutate();
  };
  const term = q.trim().toLowerCase();
  return (
    <div className="desk-page stack">
      <header className="desk-head">
        <div>
          <h1>{t('vendorsTitle')}</h1>
          <p className="muted">{t('vendorsHint')}</p>
        </div>
        {archived ? null : (
          <button type="button" className="btn btn-primary" onClick={() => setAdding((x) => !x)}>
            + {t('newVendor')}
          </button>
        )}
      </header>
      {adding ? (
        <form className="card stack-sm" onSubmit={submit} data-testid="new-vendor">
          <h2>{t('newVendor')}</h2>
          <VendorFields f={f} set={(n) => setF((c) => ({ ...c, ...n }))} idPrefix="new" />
          {add.error ? <ErrorBox error={add.error} /> : null}
          <button type="submit" className="btn btn-primary" disabled={add.isPending || !f.name.trim() || termsBad(f)}>
            {t('save')}
          </button>
        </form>
      ) : null}
      <div className="toolbar">
        <input className="tool-search" type="search" placeholder={t('search')} aria-label={t('search')} value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="tool check">
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked ? '1' : '')} />
          {t('archivedFilter')}
        </label>
      </div>
      <QueryView query={book}>
        {(vs) => {
          const shown = term ? vs.filter((v) => v.name.toLowerCase().includes(term)) : vs;
          return shown.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {shown.map((v) => (
                <VendorRow key={v.id} v={v} archived={archived} />
              ))}
            </ul>
          );
        }}
      </QueryView>
    </div>
  );
}
